import { DomainUpsert } from "@/components/domain";
import { SelectSearch } from "@/components/select-search";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CreatePayment,
  Currencies,
  paymentMethodAsArray,
  PaymentState,
  paymentStateAsArray,
} from "@/models";
import { FormOptions } from "@deadcow-enterprises/react-forms";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useGetReservationClientsWithNames } from "../-hooks/useGetReservationClientsWithName";
import { DatePicker } from "@/components/ui/date-picker";
import { Textarea } from "@/components/ui/textarea";

export function Form({
  model,
  onUpsert,
  isDeposit,
  reservationId,
}: {
  model?: CreatePayment;
  onUpsert: (data: CreatePayment) => Promise<void>;
  reservationId: string;
  isDeposit: boolean;
}) {
  const { data: clients, isLoading } =
    useGetReservationClientsWithNames(reservationId);
  const { t } = useTranslation();
  const isEdit = useMemo(() => !!model, [model]);
  const formConfig = useMemo<FormOptions<CreatePayment>>(() => {
    const config: FormOptions<CreatePayment> = {
      isDeposit: {
        value: isDeposit,
      },
      payee: {
        value: model?.payee ?? "",
        validator: z.string().min(0, "errors.required"),
      },
      state: {
        value: model?.state ?? PaymentState.No,
      },
      currency: {
        value: model?.currency,
      },
      value: {
        value: model?.value,
      },
      balance: {
        value: model?.balance,
      },
      paymentMethod: {
        value: model?.paymentMethod,
      },
      invoice: {
        value: model?.invoice,
      },
      invoiceUrl: {
        value: model?.invoiceUrl,
      },
      paymentDate: {
        value: model?.paymentDate,
      },
      notes: {
        value: model?.notes,
      },
      reservationId: {
        value: model?.reservationId ?? Number.parseInt(reservationId),
      },
    };
    return config;
  }, [isDeposit, model, reservationId]);

  const newTitle = useMemo(
    () =>
      isDeposit ? "payments.new.deposit.title" : "payments.new.balance.title",
    [isDeposit],
  );

  const updateTitle = useMemo(
    () =>
      isDeposit
        ? "payments.update.deposit.title"
        : "payments.update.balance.title",
    [isDeposit],
  );

  const newButton = useMemo(
    () =>
      isDeposit ? "payments.new.deposit.button" : "payments.new.balance.button",
    [isDeposit],
  );
  const updateButton = useMemo(
    () =>
      isDeposit
        ? "payments.update.deposit.button"
        : "payments.update.balance.button",
    [isDeposit],
  );

  return (
    <DomainUpsert
      title={isEdit ? t(updateTitle) : t(newTitle)}
      buttonTranslation={isEdit ? updateButton : newButton}
      formConfig={formConfig}
      onUpsert={onUpsert}
      fields={[
        {
          key: "payee",
          label: "payments.fields.payee",
          input: (form, id) =>
            isLoading || !clients ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <SelectSearch
                id={id}
                value={form.value.payee ?? undefined}
                onChange={(v) => {
                  form.setValue("payee", v);
                }}
                options={
                  clients.map(({ id, name }) => ({
                    value: id ?? "-1",
                    label: name ?? "-",
                  })) ?? []
                }
              />
            ),
        },
        {
          key: "state",
          label: "payments.fields.state",
          input: (form, id) => (
            <SelectSearch
              id={id}
              value={form.value.state ?? undefined}
              onChange={(v) => {
                form.setValue("state", v);
              }}
              options={
                paymentStateAsArray.map((c) => ({
                  value: c,
                  label: t(`payments.state.${c}`),
                })) ?? []
              }
            />
          ),
        },
        {
          key: "currency",
          label: "payments.fields.currency",
          input: (form, id) => (
            <SelectSearch
              id={id}
              value={form.value.currency ?? undefined}
              onChange={(v) => {
                form.setValue("currency", v);
              }}
              options={
                Currencies.map((c) => ({
                  value: c,
                  label: t(`currencies.${c}.name`),
                })) ?? []
              }
            />
          ),
        },
        {
          key: "value",
          label: "payments.fields.value",
          input: (form, id) => (
            <Input
              id={id}
              type="number"
              step={0.01}
              min={0}
              inputMode="numeric"
              value={form.value.value ?? ""}
              onChange={(e) => {
                form.setValue("value", Number.parseFloat(e.target.value));
              }}
            />
          ),
        },
        {
          key: "balance",
          label: "payments.fields.balance",
          input: (form, id) => (
            <Input
              id={id}
              type="number"
              step={0.01}
              min={0}
              inputMode="numeric"
              value={form.value.balance ?? ""}
              onChange={(e) => {
                form.setValue("balance", Number.parseFloat(e.target.value));
              }}
            />
          ),
        },
        {
          key: "paymentMethod",
          label: "payments.fields.paymentMethod",
          input: (form, id) => (
            <SelectSearch
              id={id}
              value={form.value.paymentMethod ?? undefined}
              onChange={(v) => {
                form.setValue("paymentMethod", v);
              }}
              options={
                paymentMethodAsArray.map((c) => ({
                  value: c,
                  label: t(`payments.methods.${c}`),
                })) ?? []
              }
            />
          ),
        },
        {
          key: "invoiceUrl",
          label: "payments.fields.invoice",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.invoiceUrl ?? ""}
              inputMode="url"
              onChange={(e) => {
                form.setValue("invoiceUrl", e.target.value);
              }}
            />
          ),
        },
        {
          key: "paymentDate",
          label: "payments.fields.paymentDate",
          input: (form, id) => (
            <DatePicker
              className="w-full"
              id={id}
              date={form.value.paymentDate ?? undefined}
              mode="single"
              setDate={(date) => {
                form.setValue("paymentDate", date);
              }}
            />
          ),
        },
        {
          key: "notes",
          label: "payments.fields.notes",
          input: (form, id) => (
            <Textarea
              className="w-full"
              id={id}
              value={form.value.notes ?? undefined}
              onChange={(e) => {
                form.setValue("notes", e.target.value);
              }}
            />
          ),
        },
      ]}
    />
  );
}
